import _ from "lodash";
import { action, computed, decorate, observable, reaction } from "mobx";
// just show the entire list for these, so they don't have to know specifics to search
const EXCLUDED_TYPE_AHEAD_FIELDS_NAMES = ["VenueName", "Tags"];

// A couple unique fields where FTS query info maps to these fields, but they are marked as display=false
const INCLUDED_DISPLAY_NAMES = ["PlayerId", "TeamId"];

class PitsMetadataStore {
  constructor(pitsApi, experienceStore) {
    this.pitsApi = pitsApi;
    this.experienceStore = experienceStore;
    this.fieldsDataIsLoaded = false;
    this.typesDataIsLoaded = false;

    // Field metadata may be index-specific; reload it whenever the selected searchType changes.
    reaction(
      () => this.experienceStore.searchType,
      () => this.initialize()
    );
  }

  categories = [
    { name: "General", value: "General" },
    { name: "Batter", value: "Batter" },
    { name: "Pitcher", value: "Pitcher" },
    { name: "Fielder", value: "Fielder" },
    { name: "Game", value: "Game" },
    { name: "Runner", value: "Runner" },
    { name: "Play Metadata", value: "PlayMetaData" },
    { name: "Content", value: "Content" },
    { name: "Review", value: "Review" }
  ];
  fields = {};
  types = [];

  initialize() {
    const searchType = this.experienceStore.searchType;
    if (!searchType) {
      return;
    }
    this.fieldsDataIsLoaded = false;
    this.typesDataIsLoaded = false;
    this.pitsApi
      .getFields(searchType)
      .then(
        data =>
          (this.fields = _.fromPairs(
            // Only show fields marked with display = true or fields we HAVE to support for freetext
            _.map(
              _.filter(
                data,
                field =>
                  field.display || INCLUDED_DISPLAY_NAMES.includes(field.name)
              ),
              f => {
                f.values = [];
                return [f.id, f];
              }
            )
          ))
      )
      .then(fields => {
        _.each(fields, (field, fieldId) => {
          if (
            this.isTypeAheadEnabled(field.id) === false &&
            this.fieldsWithDistinctValues.includes(field.name)
          ) {
            this.pitsApi
              .getFieldValues(fieldId, searchType)
              .then(d => (this.fields[fieldId].values = d));
          }
        });
      })
      .then(() => (this.fieldsDataIsLoaded = true));
    this.pitsApi
      .getTypes()
      .then(d => (this.types = d))
      .then(() => (this.typesDataIsLoaded = true));
  }

  getFieldValues(fieldId) {
    const searchType = this.experienceStore.searchType;
    const field = this.fields[fieldId];
    if (
      this.fieldsWithDistinctValues.includes(field.name) &&
      _.isEmpty(field.values)
    ) {
      this.pitsApi
        .getFieldValues(fieldId, searchType)
        .then(d => (this.fields[fieldId].values = d));
    }
  }

  getFieldByName(fieldName) {
    return Object.values(this.fields).find(f => f.name === fieldName);
  }

  get fieldsWithDistinctValues() {
    return _.reduce(
      this.fields,
      (acc, field) => {
        if (field.supportsDistinctValues) {
          acc.push(field.name);
        }
        return acc;
      },
      []
    );
  }

  get fieldsByCategory() {
    return _.chain(this.fields)
      .sortBy(field => field.name)
      .groupBy(field => field.category)
      .value();
  }

  get typesByName() {
    return _.fromPairs(_.map(this.types, t => [t.name, t]));
  }

  get isInitialized() {
    return this.fieldsDataIsLoaded && this.typesDataIsLoaded;
  }

  isTypeAheadEnabled(fieldId) {
    const field = this.fields[fieldId];
    return (
      field.typeAheadEnabled &&
      !EXCLUDED_TYPE_AHEAD_FIELDS_NAMES.includes(field.name)
    );
  }
}

decorate(PitsMetadataStore, {
  fields: observable,
  fieldsDataIsLoaded: observable,
  types: observable,
  typesDataIsLoaded: observable,
  setFields: action,
  setTypes: action,
  fieldsByCategory: computed,
  fieldsWithDistinctValues: computed,
  isInitialized: computed,
  typesByName: computed
});

export default PitsMetadataStore;



// WEBPACK FOOTER //
// ./src/stores/PitsMetadataStore.js